import { tokens } from '@sparkpost/design-tokens';
import { Lock, LockOpen } from '@sparkpost/matchbox-icons';
import { formatDate } from '@sparkpost/report-builder/helpers/date';
import React, { useCallback, useState } from 'react';
import { ExternalLink, SubduedLink } from 'src/components/links';
import {
  Banner,
  Box,
  Button,
  Column,
  Columns,
  LabelValue,
  Layout,
  Panel,
  Radio,
  Stack
} from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import { ERROR_CODE_CONFIG, EXTERNAL_LINKS } from '../constants';
import useDomains from '../hooks/useDomains';
import TLSMigrationModal from './TLSMigrationModal';
import TlsStatusBadge from './TlsStatusBadge';

function isRenewalDateInFuture(tlsCertificate) {
  if (!tlsCertificate?.expires_at) {
    return false;
  }
  return new Date() < new Date(tlsCertificate.expires_at);
}

export default function TrackingDomainTLSCertificateSection({ domain, isSectionVisible }) {
  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
  const [httpsConfig, setHttpsConfig] = useState('managed');
  const [isEnabling, setIsEnabling] = useState(false);
  const { trackingDomainCname, trackingDomainCnameV2, updateTrackingDomain, listTrackingDomains } =
    useDomains();

  const openMigrationModal = useCallback(() => {
    setIsMigrationModalOpen(true);
  }, []);

  const closeMigrationModal = useCallback(() => {
    setIsMigrationModalOpen(false);
  }, []);

  const handleEnableSelfManaged = useCallback(async () => {
    setIsEnabling(true);
    try {
      await updateTrackingDomain({
        domain: domain?.domain,
        subaccount: domain?.subaccount_id,
        secure: true,
        port: 443
      });
      await listTrackingDomains();
    } finally {
      setIsEnabling(false);
    }
  }, [domain, updateTrackingDomain, listTrackingDomains]);

  const selectManagedConfig = useCallback(() => {
    if (!isEnabling) {
      setHttpsConfig('managed');
    }
  }, [isEnabling]);

  const selectSelfManagedConfig = useCallback(() => {
    if (!isEnabling) {
      setHttpsConfig('self-managed');
    }
  }, [isEnabling]);

  if (!isSectionVisible) {
    return null;
  }

  const {
    uses_managed_certificate: usesManagedCertificate,
    status,
    tls_certificate: tlsCertificate,
    verified,
    secure
  } = domain;

  const tlsStatusValid = status?.tls_status === 'valid';
  const showRenewalDate = isRenewalDateInFuture(tlsCertificate);
  const errorCode = tlsCertificate?.error_code;
  const errorConfig = errorCode ? ERROR_CODE_CONFIG[errorCode] || ERROR_CODE_CONFIG.unknown : null;

  const isManagedTls = usesManagedCertificate;
  const isSelfManagedHttps = !usesManagedCertificate && secure;
  const isHttpOnly = !usesManagedCertificate && !secure;

  const managedTlsLockColor =
    status?.tls_status === 'valid' ? tokens.color_green_500 : tokens.color_gray_500;

  return (
    <Layout>
      <Layout.Section annotated>
        <Layout.SectionTitle as="h2">HTTPS</Layout.SectionTitle>
        <Stack>
          <SubduedText fontSize="200">
            Configure HTTPS for secure click and open tracking.
          </SubduedText>
          <SubduedLink
            as={ExternalLink}
            to={EXTERNAL_LINKS.TRACKING_DOMAIN_HTTPS_DOCUMENTATION}
            fontSize="200"
          >
            HTTPS Documentation
          </SubduedLink>
        </Stack>
      </Layout.Section>
      <Layout.Section>
        <Panel>
          {isHttpOnly && (
            <Panel.Section>
              <Box display="flex" alignItems="flex-start" gap="300">
                <LockOpen size={35} color={tokens.color_yellow_500} />
                <Box ml={18}>
                  <strong>HTTPS Disabled</strong>
                  <SubduedText fontSize="200" marginTop="100">
                    HTTP-only domains can be flagged by web browsers and negatively impact
                    engagement rates.
                  </SubduedText>
                </Box>
              </Box>

              <Box marginTop="500">
                <Stack space="300">
                  <Radio.Group label="Choose how to enable HTTPS:">
                    <Box
                      padding="300"
                      borderRadius="200"
                      cursor="pointer"
                      onClick={selectManagedConfig}
                    >
                      <Radio
                        disabled={isEnabling}
                        checked={httpsConfig === 'managed'}
                        onChange={selectManagedConfig}
                        label={
                          <>
                            <Box display="inline-block" paddingRight={10}>
                              Certificate managed by SparkPost (Recommended)
                            </Box>
                            <Lock size={16} marginTop="-5px" color={tokens.color_green_500} />
                          </>
                        }
                        id="https-config-managed"
                        name="httpsConfig"
                      />
                      <Box marginLeft="500" marginTop="100">
                        <SubduedText fontSize="200">
                          We&apos;ll automatically provision and renew a TLS certificate for your
                          tracking domain. Recommended for most senders.
                        </SubduedText>
                      </Box>
                    </Box>

                    <Box
                      padding="300"
                      borderRadius="200"
                      cursor="pointer"
                      onClick={selectSelfManagedConfig}
                    >
                      <Radio
                        disabled={isEnabling}
                        checked={httpsConfig === 'self-managed'}
                        onChange={selectSelfManagedConfig}
                        label={
                          <>
                            <Box display="inline-block" paddingRight={10}>
                              Certificate managed by CDN or reverse proxy (Advanced)
                            </Box>
                            <Lock size={16} marginTop="-5px" color={tokens.color_gray_500} />
                          </>
                        }
                        id="https-config-self-managed"
                        name="httpsConfig"
                      />
                      <Box marginLeft="500" marginTop="100">
                        <SubduedText fontSize="200">
                          Use your own managed infrastructure to serve a TLS certificate for your
                          domain. Requires additional setup.
                        </SubduedText>
                      </Box>
                    </Box>
                  </Radio.Group>

                  <Box>
                    {httpsConfig === 'managed' ? (
                      <Button variant="primary" onClick={openMigrationModal} disabled={isEnabling}>
                        Enable HTTPS
                      </Button>
                    ) : (
                      <>
                        <Banner status="info" marginBottom="400">
                          Please follow these instructions before enabling this option:
                          <SubduedLink
                            as={ExternalLink}
                            to={EXTERNAL_LINKS.TRACKING_DOMAIN_HTTPS_DOCUMENTATION}
                          >
                            Enabling HTTPS Engagement Tracking on SparkPost
                          </SubduedLink>
                        </Banner>
                        <Button
                          variant="primary"
                          onClick={handleEnableSelfManaged}
                          loading={isEnabling}
                        >
                          Enable HTTPS
                        </Button>
                      </>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Panel.Section>
          )}

          {isSelfManagedHttps && (
            <Panel.Section>
              <Box display="flex" alignItems="flex-start" gap="300">
                <Lock size={35} color={tokens.color_gray_500} />
                <Box ml={18}>
                  <strong>HTTPS Enabled</strong> via self-managed infrastructure
                  <SubduedText fontSize="200" marginTop="100">
                    Your domain is configured to use HTTPS through your own CDN or reverse proxy.
                    Make sure your CDN is properly configured to forward traffic to
                    {' ' + trackingDomainCname}.
                  </SubduedText>
                </Box>
              </Box>
              {status?.tls_status !== 'unavailable' && (
                <Banner
                  status="info"
                  marginTop="500"
                  title="New: Managed TLS certificates for tracking domains"
                >
                  <Box>
                    <SubduedText fontSize="200">
                      SparkPost can now automatically provision and renew a TLS certificate for your
                      tracking domain. Recommended for most senders.
                    </SubduedText>
                    <Box marginTop="300">
                      <Button variant="primary" onClick={openMigrationModal}>
                        Switch to Managed HTTPS
                      </Button>
                    </Box>
                  </Box>
                </Banner>
              )}
            </Panel.Section>
          )}

          {isManagedTls && (
            <>
              <Panel.Section>
                <Box display="flex" alignItems="flex-start" gap="300">
                  <Lock size={35} color={managedTlsLockColor} />
                  <Box ml={20}>
                    <strong>HTTPS Enabled</strong> via managed TLS certificate
                    <SubduedText fontSize="200" marginTop="100">
                      SparkPost automatically manages a TLS certificate for this tracking domain,
                      enabling secure connections for click and open tracking.
                    </SubduedText>
                  </Box>
                </Box>
                {errorConfig && (
                  <Banner status="danger" size="small" marginTop="400">
                    {errorConfig.description}
                  </Banner>
                )}
                {!verified && (
                  <Banner status="warning" size="small" marginTop="400">
                    Verify your domain to get a valid certificate.
                  </Banner>
                )}
              </Panel.Section>
              <Panel.Section>
                <Columns>
                  <Column>
                    <LabelValue>
                      <LabelValue.Label>Certificate Status</LabelValue.Label>
                      <LabelValue.Value>
                        <TlsStatusBadge tlsStatus={status?.tls_status} verified={verified} />
                      </LabelValue.Value>
                    </LabelValue>
                  </Column>
                  {tlsStatusValid && tlsCertificate?.issued_at && (
                    <Column>
                      <LabelValue>
                        <LabelValue.Label>Issued At</LabelValue.Label>
                        <LabelValue.Value>
                          {formatDate.useMomentInput(tlsCertificate.issued_at)}
                        </LabelValue.Value>
                      </LabelValue>
                    </Column>
                  )}
                  {tlsStatusValid && showRenewalDate && (
                    <Column>
                      <LabelValue>
                        <LabelValue.Label>Next Renewal</LabelValue.Label>
                        <LabelValue.Value>
                          {formatDate.useMomentInput(tlsCertificate.expires_at)}
                        </LabelValue.Value>
                      </LabelValue>
                    </Column>
                  )}
                </Columns>
              </Panel.Section>
            </>
          )}
        </Panel>
      </Layout.Section>

      <TLSMigrationModal
        domain={domain}
        isOpen={isMigrationModalOpen}
        onClose={closeMigrationModal}
        cnameV2={trackingDomainCnameV2}
        sourceType={isHttpOnly ? 'http-only' : 'self-managed'}
      />
    </Layout>
  );
}
